import { Player } from '@lordicon/react';

import { useCallback, useEffect, useRef, useState } from 'react';

import AppLogger from 'utils/AppLogger';

const iconCache = {};

/**
 * @typedef {object} LordIconProps
 * @params {Object} props
 * @property {string} [props.src] - The url of the JSON file that contains the icon data.
 * @property {string} [props.primaryColor] - The primary color of the icon.
 * @property {string} [props.secondaryColor] - The secondary color of the icon.
 * @property { current: HTMLElement | null } [props.target] - The target element that triggers the animation.
 * @property {number|string} [props.width=32] - The width of the icon.
 * @property {number|string} [props.height=32] - The height of the icon.
 * @property {string} [props.color] - The color of the icon.
 * @property {boolean} [props.loop=false] - Whether the icon should loop.
 * @property {number} [props.delay=0] - The delay before the icon loops.
 * @property {object} [props.style] - The style object of the icon.
 * @returns {JSX.Element}
 */

const LordIcon = (props) => {
	const {
		src,
		primaryColor,
		secondaryColor,
		target,
		width = 32,
		height = 32,
		color,
		loop = false,
		delay = 0,
		style
	} = props;
	const playerRef = useRef(null);
	const [iconData, setIconData] = useState(null);

	useEffect(() => {
		const fetchIcon = async () => {
			try {
				if (iconCache[src]) {
					setIconData(iconCache[src]);
				} else {
					const response = await fetch(src);
					const data = await response.json();
					iconCache[src] = data;
					setIconData(data);
				}
			} catch (error) {
				AppLogger.error(error);
			}
		};
		fetchIcon();
	}, []);

	useEffect(() => {
		const targetRef = target?.current;
		const player = playerRef?.current;
		if (player && targetRef) {
			const handleHover = () => {
				player.playFromBeginning();
			};

			targetRef.addEventListener('mouseenter', handleHover);

			return () => {
				targetRef.removeEventListener('mouseenter', handleHover);
			};
		}
	}, []);

	const handleMouseEnter = useCallback(() => {
		if (!loop && playerRef.current) {
			playerRef.current.playFromBeginning();
		}
	}, [loop]);

	const handleComplete = useCallback(() => {
		if (loop) {
			setTimeout(() => {
				playerRef.current?.playFromBeginning();
			}, delay);
		}
	}, [loop, delay]);

	const handleOnReady = useCallback(() => {
		if (iconData && loop) {
			playerRef.current?.playFromBeginning();
		}
	}, [iconData, loop]);

	return (
		<div
			className="lord-icon"
			onMouseEnter={handleMouseEnter}
			style={{
				...style,
				width,
				height
			}}>
			{iconData ? (
				<Player
					{...(color ? { colorize: color } : {})}
					{...(primaryColor && secondaryColor
						? { colors: `primary:${primaryColor},secondary:${secondaryColor}` }
						: {})}
					ref={playerRef}
					size={'100%'}
					icon={iconData}
					onComplete={handleComplete}
					onReady={handleOnReady}
				/>
			) : null}
		</div>
	);
};

export default LordIcon;
